import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useEmployeesMap } from '@/hooks/useEmployeesMap';
import { getAssignee, isAssigneeHidden } from '@/helpers/assignee';
import { Asset } from '@/types/assets';

/** Resolves an asset's assignee the way `getAssignee` does, bound to the current map and flag. */
export const useGetAssignee = () => {
  const { ithDivisions } = useFlags();
  const { getEmployeeById } = useEmployeesMap();
  const isDivisionsEnabled = Boolean(ithDivisions);

  return useCallback(
    (asset: Asset) =>
      getAssignee(asset, { isDivisionsEnabled, getEmployeeById }),
    [isDivisionsEnabled, getEmployeeById]
  );
};

/**
 * True when the asset's assignee is outside the user's divisions; see
 * `isAssigneeHidden`. Without an employees map (no `employees:read`) visibility
 * can't be checked, and the backend already scopes what the page received, so
 * nothing counts as hidden.
 */
export const useIsAssigneeHidden = () => {
  const { isEnabled, getEmployeeById } = useEmployeesMap();

  return useCallback(
    (asset: Asset) =>
      isAssigneeHidden(asset, {
        isDivisionsEnabled: isEnabled,
        getEmployeeById,
      }),
    [isEnabled, getEmployeeById]
  );
};

/**
 * For surfaces that already show the employee (asset details): undefined when
 * the assignee is hidden by divisions, the map's employee when there is one,
 * and the payload when there is no map to check against.
 */
export const useGetVisibleAssignee = () => {
  const getAssignee = useGetAssignee();
  const isAssigneeHidden = useIsAssigneeHidden();

  return useCallback(
    (asset: Asset) =>
      isAssigneeHidden(asset)
        ? undefined
        : (getAssignee(asset) ?? asset.assignedToEmployee),
    [getAssignee, isAssigneeHidden]
  );
};

/**
 * For surfaces that already show the employee (profile, asset details): the map
 * can be empty there (no `employees:read`), which must not read as "no plan",
 * so the payload value is the fallback.
 */
export const useGetAssigneePackageId = () => {
  const getAssignee = useGetAssignee();

  return useCallback(
    (asset: Asset) =>
      getAssignee(asset)?.organizationPackageId ??
      asset.assignedToEmployee?.organizationPackageId,
    [getAssignee]
  );
};
